import React, { memo } from "react";
import { Link } from "react-router-dom";

/**
 * "Needs attention" card shown under the Recording Quality rows when a result
 * has a quality issue. Styled like the activity-feed cards (rounded, elevated)
 * with an amber info glyph leading the first line of copy so it reads as a
 * gentle prompt, and a compact "Tips" link in the bottom-right that deep-links
 * to the mode guidance. Renders nothing when there is neither a message nor a
 * hint.
 */
function ResultQualityNotice({
  messages = [],
  hints = [],
  hintsTitle = "",
  linkTo = "",
  onLinkClick = null,
  ctaLabel = "",
}) {
  const noticeMessages = Array.isArray(messages)
    ? messages.filter(Boolean)
    : [];
  const noticeHints = Array.isArray(hints) ? hints.filter(Boolean) : [];

  if (!noticeMessages.length && !noticeHints.length) {
    return null;
  }

  // The icon pairs with whichever copy leads the card instead of heading the
  // whole text column, so the rest runs full width and no gap opens up beneath
  // the glyph. Hint-only notices promote the hints title into that row.
  const [headerMessage, ...remainingMessages] = noticeMessages;
  const headerUsesHintsTitle = !headerMessage && Boolean(hintsTitle);

  return (
    <div className="w-full rounded-2xl bg-card px-2 py-3 shadow flex flex-col gap-2 text-left">
      <div className="flex items-center gap-3">
        <span
          className="material-symbols-outlined text-[22px] leading-none text-amber-500"
          aria-hidden="true"
        >
          info
        </span>

        {headerMessage ? (
          <p className="min-w-0 flex-1 text-sm text-typography leading-relaxed">
            {headerMessage.title} {headerMessage.subtitle}
          </p>
        ) : headerUsesHintsTitle ? (
          <p className="min-w-0 flex-1 text-typography font-medium">
            {hintsTitle}
          </p>
        ) : null}
      </div>

      {remainingMessages.map((message, index) => (
        <p
          key={`${message.title || "message"}-${index}`}
          className="text-sm text-typography leading-relaxed"
        >
          {message.title} {message.subtitle}
        </p>
      ))}

      {noticeHints.length ? (
        <div className="w-full">
          {hintsTitle && !headerUsesHintsTitle ? (
            <p className="text-typography font-medium mb-1">{hintsTitle}</p>
          ) : null}
          <ul className="list-disc pl-5 flex flex-col gap-1 marker:text-greyscale-3">
            {noticeHints.map((hint) => (
              <li key={hint} className="text-sm text-typography">
                {hint}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {linkTo ? (
        <Link
          to={linkTo}
          className="self-end inline-flex items-center gap-0.5 text-sm font-medium text-primary"
          onClick={onLinkClick || undefined}
        >
          {ctaLabel}
          <span
            className="material-symbols-outlined text-[18px] leading-none"
            aria-hidden="true"
          >
            chevron_right
          </span>
        </Link>
      ) : null}
    </div>
  );
}

export default memo(ResultQualityNotice);
